import React, { useEffect, useState } from "react";
import { listDivisions, listDistrictsByDivision, listThanasByDistrict, listPostOfficesByThana } from "../api/locationApi";
import "./LocationPicker.css";

/**
 * Reusable cascading division → district → thana → post office picker,
 * backed by `/api/locations/*` (see digicampusbd's `LocationService`).
 * Reports the full selection up via `onChange({ divisionId, districtId,
 * thanaId, postOfficeId })` whenever any level changes — future address
 * forms (student/staff registration, institute setup, ...) consume this
 * instead of re-implementing the cascade.
 */
export default function LocationPicker({ value, onChange, disabled }) {
  const selected = value || {};

  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [thanas, setThanas] = useState([]);
  const [postOffices, setPostOffices] = useState([]);

  useEffect(() => {
    listDivisions().then(setDivisions).catch(() => setDivisions([]));
  }, []);

  useEffect(() => {
    if (!selected.divisionId) {
      setDistricts([]);
      return;
    }
    listDistrictsByDivision(selected.divisionId).then(setDistricts).catch(() => setDistricts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.divisionId]);

  useEffect(() => {
    if (!selected.districtId) {
      setThanas([]);
      return;
    }
    listThanasByDistrict(selected.districtId).then(setThanas).catch(() => setThanas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.districtId]);

  useEffect(() => {
    if (!selected.thanaId) {
      setPostOffices([]);
      return;
    }
    listPostOfficesByThana(selected.thanaId).then(setPostOffices).catch(() => setPostOffices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.thanaId]);

  const update = (patch) => onChange({ ...selected, ...patch });

  return (
    <div className="location-picker">
      <select
        disabled={disabled}
        value={selected.divisionId || ""}
        onChange={(e) => update({ divisionId: e.target.value || null, districtId: null, thanaId: null, postOfficeId: null })}
      >
        <option value="">Division…</option>
        {divisions.map((d) => (
          <option key={d.divisionId} value={d.divisionId}>
            {d.divisionName}
          </option>
        ))}
      </select>

      <select
        disabled={disabled || !selected.divisionId}
        value={selected.districtId || ""}
        onChange={(e) => update({ districtId: e.target.value || null, thanaId: null, postOfficeId: null })}
      >
        <option value="">District…</option>
        {districts.map((d) => (
          <option key={d.districtId} value={d.districtId}>
            {d.districtName}
          </option>
        ))}
      </select>

      <select
        disabled={disabled || !selected.districtId}
        value={selected.thanaId || ""}
        onChange={(e) => update({ thanaId: e.target.value || null, postOfficeId: null })}
      >
        <option value="">Thana…</option>
        {thanas.map((t) => (
          <option key={t.thanaId} value={t.thanaId}>
            {t.thanaName}
          </option>
        ))}
      </select>

      <select
        disabled={disabled || !selected.thanaId}
        value={selected.postOfficeId || ""}
        onChange={(e) => update({ postOfficeId: e.target.value || null })}
      >
        <option value="">Post Office…</option>
        {postOffices.map((p) => (
          <option key={p.postOfficeId} value={p.postOfficeId}>
            {p.postOfficeName} {p.postalCode ? `(${p.postalCode})` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
